import * as React from 'react';

import { useNavigate } from 'react-router-dom';

const APP_ROOT_PATH = '/webadmin/app';

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object';
}

function isSafeAppPathname(pathname: string): boolean {
  return pathname === APP_ROOT_PATH || pathname.startsWith(`${APP_ROOT_PATH}/`);
}

function normalizeHistoryIndex(historyState: unknown): number | null {
  if (!isObjectRecord(historyState)) return null;
  const idx = historyState.idx;
  if (typeof idx !== 'number' || !Number.isFinite(idx)) return null;
  return idx;
}

export function canUseBrowserBack(
  historyState: unknown,
  historyLength: number | null | undefined,
): boolean {
  const idx = normalizeHistoryIndex(historyState);
  if (idx != null) return idx > 0;
  if (typeof historyLength !== 'number' || !Number.isFinite(historyLength)) return false;
  return historyLength > 1;
}

export function resolveSafeAppBackTarget(candidate: string | null | undefined): string | null {
  if (typeof candidate !== 'string') return null;
  const trimmed = candidate.trim();
  if (!trimmed) return null;
  if (!trimmed.startsWith('/')) return null;

  try {
    const normalized = new URL(trimmed, 'https://erp24.local');
    if (!isSafeAppPathname(normalized.pathname)) return null;
    return `${normalized.pathname}${normalized.search}${normalized.hash}`;
  } catch {
    return null;
  }
}

export function resolveSmartBackFallbackTarget(options: {
  candidates?: Array<string | null | undefined>;
  fallback: string;
}): string {
  const { candidates = [], fallback } = options;
  for (const candidate of candidates) {
    const safeCandidate = resolveSafeAppBackTarget(candidate);
    if (safeCandidate) return safeCandidate;
  }
  return resolveSafeAppBackTarget(fallback) ?? APP_ROOT_PATH;
}

export type SmartBackOptions = {
  candidates?: Array<string | null | undefined>;
  fallback: string;
  replaceOnFallback?: boolean;
};

export type SmartBackNavigate = (options: SmartBackOptions) => void;

export function useSmartBackNavigate(): SmartBackNavigate {
  const navigate = useNavigate();

  return React.useCallback(
    (options: SmartBackOptions) => {
      const hasWindow = typeof window !== 'undefined';
      const hasBrowserBack = hasWindow && canUseBrowserBack(window.history.state, window.history.length);
      if (hasBrowserBack) {
        navigate(-1);
        return;
      }

      const target = resolveSmartBackFallbackTarget(options);
      navigate(target, { replace: options.replaceOnFallback ?? true });
    },
    [navigate],
  );
}
